import type { Product, ProjectProduct } from '/@/common/things/types'
import type { Vendor } from '/@/common/products/types'
import { useCurrentDesigner } from '/@/data/designer/current'
import { getUrlDomain, truncateUrl } from '/@/utils/misc'

export type VendorLike = Pick<
  Vendor,
  'id' | 'isVerified' | 'name' | 'affiliate'
>

export type ProductLike = Pick<
  Product,
  | 'id'
  | 'name'
  | 'isVerified'
  | 'minPriceCent'
  | 'maxPriceCent'
  | 'category'
  | 'categoryKey'
  | 'addedByDesignerId'
  | 'vendorUrl'
  | 'cleanVendorUrl'
> & {
  vendor?: VendorLike | undefined | null
}

export function useProductTracking() {
  const { designer } = useCurrentDesigner()

  function vendorTrackingProps(vendor: VendorLike | undefined | null) {
    if (!vendor) return {}

    return {
      brandId: vendor.id,
      brandName: vendor.name,
      isBrandVerified: vendor.isVerified,
      affiliate: vendor.affiliate,
    }
  }

  function productTrackingProps(
    thing:
      | ProductLike
      | (Pick<
          ProjectProduct,
          'product' | 'category' | 'cleanUrl' | 'affiliateUrl'
        > & {
          vendor?: VendorLike | undefined | null
        })
      | undefined
      | null,
  ) {
    if (!thing) return {}

    let vendor = thing.vendor
    let categoryKey = thing.category?.name
    let vendorUrl
    if ('product' in thing) {
      vendor = thing.vendor ?? thing.product.vendor
      categoryKey =
        thing.category?.name ??
        thing.product.category?.name ??
        thing.product.categoryKey
      vendorUrl =
        thing.affiliateUrl ?? thing.cleanUrl ?? thing.product.cleanVendorUrl
      thing = thing.product
    } else {
      categoryKey = categoryKey ?? thing.categoryKey
      vendorUrl = thing.cleanVendorUrl
    }

    return {
      productId: thing.id,
      productName: thing.name,
      isProductVerified: thing.isVerified,
      category: categoryKey,
      minPrice: thing.minPriceCent ? thing.minPriceCent / 100 : null,
      maxPrice: thing.maxPriceCent ? thing.maxPriceCent / 100 : null,
      addedBy: thing.addedByDesignerId,
      addedBySelf:
        designer.value?.id && thing.addedByDesignerId === designer.value?.id,
      url: vendorUrl ? truncateUrl(vendorUrl) : null,
      urlDomain: vendorUrl ? getUrlDomain(vendorUrl) : null,
      ...vendorTrackingProps(vendor),
    }
  }

  return { productTrackingProps, vendorTrackingProps }
}
